import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

// hooks and utils
import useAuth from "hooks/useAuth";
import useProductsTable from "@/views/admin/products/hooks/useProductsTable";

// functionality helpers
import { getFallbackState } from "./utils/getFallbackState";

// reusable components
import EmptyStateCard from "@/components/ui/EmptyStateCard";
import { SecureTableShell } from "@/components/ui/SecureTableShell";
import SearchFilterControls from "./components/SeachFilterControls";
import { useStorePath } from "@hooks/useStorePath";

const Products = () => {
  // TODO: Remove this after properly connecting the products form. Its temporary.
  const navigate = useNavigate();
  const { storePath } = useStorePath();

// connect to the products hook
  const { 
    productColumns, 
    renderProductRow,
    allProducts, 
    products: filteredProducts,
    searchTerm,
    updateSearchTerm,
    statusFilter,
    updateStatusFilter,
    isError,
    isLoading,
    refetch 
  } = useProductsTable();

  // Get the fallback status from the 'getFallbackState' helper
  const fallback = getFallbackState({
    isLoading,
    allProducts,
    filteredProducts,
  });

  // TODO: If we are querying from API, then implement add a guard on min input length, server side caching/pre-indexing, abortController to cancel in flight requests on new input.
  // debounce the search and filter functionality
  const [rawSearchTerm, setRawSearchTerm] = useState(searchTerm);

  useEffect(() => {
    // put a debounce delay of 300ms
    const timeout = setTimeout(() =>{
      updateSearchTerm(rawSearchTerm);
    }, 600);

    return () => clearTimeout(timeout);
  }, [rawSearchTerm, updateSearchTerm]);

  // route to a new product form
  const handleNewProduct = () => {
    navigate(storePath("products/new"));
  };

  // check if user is authenticated
  const { isAuthenticated } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/auth/sign-in" replace />;
  }

  // check for error when displaying data
  // TODO: Extend this fallback to show error codes or something more precise
  if (isError) {
    return (
      <div className="p-6">
        <EmptyStateCard
          icon={
            // TODO: Put a proper icon for error
            <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-full">
              <span className="text-red-600 font-bold text-lg">!</span>
            </div>
          }
          title="Something went wrong"
          description="We couldn't load your products. Please try again or contact support."
          buttonLabel="Retry"
          // TODO: Reroute accordingly
          onAction={refetch}
        />
      </div>
    );
  }

  return (
    <div className="pt-[2em]">
      <div className="flex flex-row justify-end items-center mb-[1em]">
        <button 
          className="bg-[#000] text-white text-[14px] px-3 py-2 rounded-lg font-bold hover:bg-[#424242]"
          onClick={handleNewProduct}
        >
          New Product
        </button>
      </div>

      {/* searching and filtering logic */}
      <SearchFilterControls
        searchTerm={rawSearchTerm}
        updateSearchTerm={setRawSearchTerm}
        statusFilter={statusFilter}
        updateStatusFilter={updateStatusFilter}
      />

      {/* Product table */}
      <SecureTableShell
        data={filteredProducts}
        columns={productColumns}
        renderRow={renderProductRow}
        fallbackState={fallback.state}
      />
    </div>
  );
};

export default Products;